import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import useOutsideClick from 'useOutsideClick';
import { useDispatch, useSelector } from 'react-redux';
import { Label, Button, PortalWrapper, Utils } from 'mw-style-react';
import cn from 'classnames';
import AppUtils from '@control-front-end/utils/utils';
import {
  CREATE_ACTOR_FILTER,
  UPDATE_ACTOR_FILTER,
  GET_ACTOR_FILTERS,
  CLEAR_ACTOR_FILTERS,
} from '@control-front-end/common/constants/graphActors';
import Toggle from '@control-front-end/common/components/Toggle';
import { SET_MODAL } from 'constants';
import useIntl from 'useIntl';
import mes from './intl';
import FilterSelect from './components/FilterSelect';
import TemplateFields from './components/TemplateFields';
import DateRange from './components/DateRange';
import './ActorsFiltersPopup.scss';

function ActorsFiltersPopup(props) {
  const {
    value: curFilters,
    panelMode,
    onChange,
    children,
    type,
    mode = 'list',
    btns = ['search', 'save'],
  } = props;
  const t = useIntl();
  const filtersBox = useRef();
  const dispatch = useDispatch();
  const [errors, setErrors] = useState({});
  const [popup, togglePopup] = useState(false);
  const [selectedFormTitle, setFormTitle] = useState('');
  const savedFilters = useSelector((state) => state.actorsFilters.list);
  const [filters, setFilters] = useState({});
  useOutsideClick({
    ref: filtersBox,
    callback: (e) => {
      const exceptionIds = ['btnSaveFilter', 'btnDeleteFilter'];
      if (!exceptionIds.includes(e.target.id)) togglePopup(false);
    },
  });
  /**
   *  Преобразование фильтра из API в модель для компонента
   */
  const convertFilter = (filter) => {
    let typeF = 'all';
    if (filter.isSystem) {
      const isSystem = AppUtils.toBool(filter.isSystem);
      typeF = isSystem ? 'system' : 'custom';
    }
    return {
      ...filter,
      type: typeF,
      systemObjStatus: filter.systemObjStatus || 'all',
      systemObjType: filter.systemObjType,
    };
  };

  useEffect(() => {
    dispatch({ type: GET_ACTOR_FILTERS.REQUEST });
    return () => {
      dispatch({ type: CLEAR_ACTOR_FILTERS });
    };
  }, []);
  useEffect(() => {
    const newFilters = convertFilter(filters);
    setFilters(newFilters);
  }, [curFilters]);

  /**
   *  Получение сохраненного фильтра по его id
   */
  const getSavedFilter = (id) => {
    return savedFilters.find((item) => item.id === id);
  };
  /**
   * Изменение параметра фильтра
   */
  const handleChangeFilterField = (data) => {
    const { id, value, formTitle, error } = data;
    const newFilters = { ...filters };
    newFilters[id] = value;
    if (id === 'formId') newFilters.q = '';
    setFilters(newFilters);
    if (formTitle) setFormTitle(formTitle);
    const newErrors = { ...errors };
    if (error) {
      newErrors[id] = error;
    } else {
      delete newErrors[id];
    }
    setErrors(newErrors);
  };

  /**
   * Изменение всего фильтра
   */
  const handleChangeFilter = ({ value }) => {
    const newFilter = {
      filterId: value.id,
      ...value.data,
      name: value.name,
    };
    setFilters(convertFilter(newFilter));
    setErrors({});
  };

  /**
   * Проверка фильтров на ошибки
   */
  const checkErrors = () => {
    let error = false;
    const reqFields = ['formId'];
    reqFields.forEach((key) => {
      if (!Object.keys(filters).includes(key) || !filters[key]) {
        errors[key] = 'fieldRequired';
        error = true;
      }
    });
    if (filters.from > filters.to) {
      errors.to = 'invalidRange';
      error = true;
    }
    const newErrors = { ...errors };
    setErrors(newErrors);
    return error;
  };

  /**
   * Изменение диапазона дат
   */
  const handleChangeDate = (range) => {
    const newFilters = { ...filters, ...range };
    setFilters(newFilters);
    if (newFilters.from > newFilters.to) {
      errors.to = 'invalidRange';
    } else {
      delete errors.to;
    }
    const newErrors = { ...errors };
    setErrors(newErrors);
  };

  /**
   * Приводим фильтры в корректный формат
   */
  const normalizeFilters = () => {
    const copyFilters = { ...filters };
    if (copyFilters.type === 'custom') {
      copyFilters.isSystem = 'false';
      delete copyFilters.systemObjStatus;
      delete copyFilters.systemObjType;
    } else if (copyFilters.type === 'system') {
      copyFilters.isSystem = 'true';
      if (copyFilters.systemObjStatus === 'all') {
        delete copyFilters.systemObjStatus;
      }
    } else {
      delete copyFilters.isSystem;
      delete copyFilters.systemObjStatus;
      delete copyFilters.systemObjType;
    }
    delete copyFilters.type;
    return copyFilters;
  };

  /**
   * Сбросить фильтры
   */
  const handleResetFilters = () => {
    setFilters({});
    onChange({});
  };

  /**
   * Применить фильтры
   */
  const deployFilters = (filter) => {
    const hasError = checkErrors();
    if (hasError) return false;
    const newFilter = filter && filter.formId ? filter : normalizeFilters();
    if (filter && filter.name) newFilter.name = filter.name;
    onChange(newFilter);
    setFilters(convertFilter(newFilter));
    return true;
  };
  /**
   * Применить все фильтры
   */
  const handleSubmitFilters = (filter) => {
    deployFilters(filter);
  };

  /**
   * Применить все фильтры и закрыть модальное окно
   */
  const handleSubmitFiltersAndClose = (filter) => {
    const deployed = deployFilters(filter);
    if (!deployed) return;
    if (mode === 'graph') Utils.toStorage('graphFoldersViewMode', mode);
    setTimeout(() => togglePopup(false), 0);
  };

  /**
   * Открытие модального окна для сохранения фильтра
   */
  const handleSaveFilter = () => {
    const hasError = checkErrors();
    if (hasError) return;
    const newFilters = normalizeFilters();
    dispatch({
      type: SET_MODAL,
      payload: {
        name: 'SaveFilter',
        data: newFilters,
        callback: handleSubmitFiltersAndClose,
      },
    });
  };

  /**
   * Сохранение существующего фильтра
   */
  const handleUpdateFilter = () => {
    const hasError = checkErrors();
    if (hasError) return;
    const filterToUpdate = {
      ...getSavedFilter(filters.filterId),
      data: filters,
    };
    dispatch({
      type: UPDATE_ACTOR_FILTER.REQUEST,
      payload: { ...filterToUpdate },
      callback: handleSubmitFiltersAndClose,
    });
  };

  /**
   * Сохранить фильтр и выбрать
   */
  const handleSaveAndSelect = () => {
    const hasError = checkErrors();
    if (hasError) return;
    const newFilters = normalizeFilters();
    dispatch({
      type: CREATE_ACTOR_FILTER.REQUEST,
      payload: { name: `Filter ${selectedFormTitle}`, data: newFilters },
      callback: (f) => {
        handleSubmitFilters({ id: f.id, title: f.name, ...f.data });
        togglePopup(false);
      },
    });
  };

  const renderOpenBtn = () => {
    const openPopup = (e) => {
      e.stopPropagation();
      togglePopup(!popup);
      if (!popup) {
        setFilters(curFilters);
      }
    };
    if (children) return <div onClick={openPopup}>{children}</div>;
    return (
      <Button
        styleName="ab__btn custom"
        type={popup ? 'default' : 'tertiary'}
        rounded={mode === 'list'}
        size="smallplus"
        icon="filter"
        onClick={openPopup}
      />
    );
  };
  return (
    <div styleName="filters">
      {renderOpenBtn()}
      {popup ? (
        <PortalWrapper
          root={document.getElementById('mainRoot')}
          vAlign="top"
          hAlign="right"
          offsetX={panelMode ? 300 : 24}
          offsetY={24}
        >
          <div styleName={cn({ filters__bg: type === 'modal' })}>
            <div
              ref={filtersBox}
              styleName={cn('filters__popup', {
                right: !!filters.formId,
                modal: type === 'modal',
              })}
            >
              <div styleName="filters__popup__header">
                <div
                  styleName="filters__popup__header__btn"
                  onClick={() => togglePopup(false)}
                >
                  {t(mes.actorsFiltersTitleCancel)}
                </div>
                <div styleName="filters__popup__header__title">
                  <Label
                    value={t(mes.actorsFiltersTitle)}
                    fontSize="large"
                    fontWeight="semibold"
                  />
                </div>
                <div
                  styleName="filters__popup__header__btn"
                  onClick={() => setFilters({})}
                >
                  {t(mes.resetFilters)}
                </div>
              </div>
              <div styleName="filters__popup__content">
                <FilterSelect
                  filters={filters}
                  savedFilters={savedFilters}
                  onChange={handleChangeFilter}
                  getSavedFilter={getSavedFilter}
                  handleSubmitFilters={handleSubmitFilters}
                  handleResetFilters={handleResetFilters}
                />
                <Toggle isOpen={true} title={t(mes.advancedSettings)}>
                  <TemplateFields
                    formId={filters.formId}
                    q={filters.q}
                    errors={errors}
                    onChange={handleChangeFilterField}
                  />
                </Toggle>
                <Toggle isOpen={true} title={t(mes.actorDateRange)}>
                  <DateRange
                    from={filters.from}
                    to={filters.to}
                    errors={errors}
                    onChange={handleChangeDate}
                  />
                </Toggle>
              </div>
              <div styleName="filters__popup__footer">
                <Button
                  type="tertiary"
                  size="large"
                  fontWeight="normal"
                  label={
                    filters.filterId
                      ? t(mes.updateActorsFilter)
                      : t(mes.saveActorsFilter)
                  }
                  onClick={
                    filters.filterId ? handleUpdateFilter : handleSaveFilter
                  }
                  visibility={btns.includes('save') ? 'visible' : 'hidden'}
                />
                <Button
                  onClick={handleSubmitFiltersAndClose}
                  size="large"
                  fontWeight="normal"
                  label={t(mes.submitSearchActors)}
                  visibility={btns.includes('search') ? 'visible' : 'hidden'}
                />
                <Button
                  onClick={handleSaveAndSelect}
                  size="large"
                  fontWeight="normal"
                  label={t(mes.createAndSelect)}
                  visibility={
                    btns.includes('saveSelect') ? 'visible' : 'hidden'
                  }
                />
              </div>
            </div>
          </div>
        </PortalWrapper>
      ) : null}
    </div>
  );
}

ActorsFiltersPopup.propTypes = {
  value: PropTypes.object,
  panelMode: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  children: PropTypes.node,
  type: PropTypes.oneOf(['popup', 'modal']),
  btns: PropTypes.array,
};

export default ActorsFiltersPopup;
